import axios from 'axios';

const API_URL = 'https://api.azalega-academy.com/api/v1';
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Courses
export const getCourses = (params) => api.get('/courses', { params });
export const getCourse = (id) => api.get(`/courses/${id}`);
export const createCourse = (data) => api.post('/courses', data);
export const updateCourse = (id, data) => api.put(`/courses/${id}`, data);
export const deleteCourse = (id) => api.delete(`/courses/${id}`);
export const publishCourse = (id, published) => api.put(`/courses/${id}/publish`, { published });
export const uploadThumbnail = (id, formData) => api.put(`/courses/${id}/thumbnail`, formData);

// Ebooks
export const uploadEbook = (id, formData) => api.put(`/courses/${id}/ebook`, formData);
export const downloadEbook = (id) => api.get(`/courses/${id}/ebook/download`);
export const downloadEbookFile = (id, params = {}) => api.get(`/courses/${id}/ebook/download/file`, {
  params,
  responseType: 'blob'
});

// Multi-chapter Ebooks
export const uploadChapter = (id, formData) => api.post(`/courses/${id}/chapters`, formData);
export const getChapter = (id, chapterId) => api.get(`/courses/${id}/chapters/${chapterId}`);
export const downloadChapterFile = (id, chapterId, params = {}) => api.get(`/courses/${id}/chapters/${chapterId}/file`, {
  params,
  responseType: 'blob'
});
export const updateChapter = (id, chapterId, formData) => api.put(`/courses/${id}/chapters/${chapterId}`, formData);
export const deleteChapter = (id, chapterId) => api.delete(`/courses/${id}/chapters/${chapterId}`);
export const reorderChapters = (id, data) => api.put(`/courses/${id}/chapters/reorder`, data);

// Video Upload
export const uploadVideo = (courseId, formData) => api.put(`/courses/${courseId}/upload-video`, formData);

// Modules
export const addModule = (courseId, data) => api.post(`/courses/${courseId}/modules`, data);
export const updateModule = (courseId, moduleId, data) => api.put(`/courses/${courseId}/modules/${moduleId}`, data);
export const deleteModule = (courseId, moduleId) => api.delete(`/courses/${courseId}/modules/${moduleId}`);

// Lessons
export const addLesson = (courseId, moduleId, data) => api.post(`/courses/${courseId}/modules/${moduleId}/lessons`, data);
export const updateLesson = (courseId, moduleId, lessonId, data) => api.put(`/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`, data);
export const deleteLesson = (courseId, moduleId, lessonId) => api.delete(`/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`);

// Enrollments
export const enrollCourse = (courseId) => api.post('/enrollments', { courseId });
export const getMyEnrollments = (params) => api.get('/enrollments', { params });
export const getEnrollment = (id) => api.get(`/enrollments/${id}`);
export const unenrollCourse = (id) => api.delete(`/enrollments/${id}`);
export const getCourseEnrollments = (courseId) => api.get(`/enrollments/course/${courseId}`);

// Progress
export const getProgress = (enrollmentId) => api.get(`/progress/enrollment/${enrollmentId}`);
export const updateProgress = (enrollmentId, lessonId, data) => api.put(`/progress/enrollment/${enrollmentId}/lesson/${lessonId}`, data);
export const completeLesson = (enrollmentId, lessonId) => api.post(`/progress/enrollment/${enrollmentId}/lesson/${lessonId}/complete`);
export const getLessonProgress = (enrollmentId, lessonId) => api.get(`/progress/enrollment/${enrollmentId}/lesson/${lessonId}`);

// Quizzes
export const createQuiz = (data) => api.post('/quizzes', data);
export const getQuiz = (id) => api.get(`/quizzes/${id}`);
export const updateQuiz = (id, data) => api.put(`/quizzes/${id}`, data);
export const deleteQuiz = (id) => api.delete(`/quizzes/${id}`);
export const submitQuiz = (id, data) => api.post(`/quizzes/${id}/submit`, data);
export const getQuizSubmissions = (id) => api.get(`/quizzes/${id}/submissions`);
export const getMyQuizSubmissions = (enrollmentId) => api.get(`/quizzes/my-submissions/enrollment/${enrollmentId}`);

// Users (Admin)
export const getUsers = (params) => api.get('/users', { params });
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Auth (Self)
export const getMe = () => api.get('/auth/me'); // Added getMe helper

// Reading Progress
export const getAllReadingProgress = () => api.get('/reading-progress');
export const getReadingProgress = (ebookId) => api.get(`/reading-progress/ebook/${ebookId}`);
export const updateReadingProgress = (ebookId, data) => api.put(`/reading-progress/ebook/${ebookId}`, data);
export const deleteReadingProgress = (ebookId) => api.delete(`/reading-progress/ebook/${ebookId}`);
export const getReadingStats = () => api.get('/reading-progress/stats');

// Bookmarks
export const getAllBookmarks = () => api.get('/bookmarks');
export const getBookmarks = (ebookId) => api.get(`/bookmarks/ebook/${ebookId}`);
export const getBookmark = (bookmarkId) => api.get(`/bookmarks/${bookmarkId}`);
export const createBookmark = (data) => api.post('/bookmarks', data);
export const updateBookmark = (bookmarkId, data) => api.put(`/bookmarks/${bookmarkId}`, data);
export const deleteBookmark = (bookmarkId) => api.delete(`/bookmarks/${bookmarkId}`);
export const deleteAllBookmarksForEbook = (ebookId) => api.delete(`/bookmarks/ebook/${ebookId}`);

// Video Progress
export const saveVideoProgress = (data) => api.post('/video-progress', data);
export const getVideoProgress = (courseId) => api.get(`/video-progress/${courseId}`);
export const getVideoLessonProgress = (courseId, lessonId) => api.get(`/video-progress/${courseId}/${lessonId}`);
export const markVideoLessonComplete = (courseId, lessonId) => api.put(`/video-progress/${courseId}/${lessonId}/complete`);
export const getCourseVideoStats = (courseId) => api.get(`/video-progress/stats/${courseId}`);

// Webinars
export const getWebinars = (params) => api.get('/webinars', { params });
export const getWebinar = (id) => api.get(`/webinars/${id}`);
export const createWebinar = (data) => api.post('/webinars', data);
export const updateWebinar = (id, data) => api.put(`/webinars/${id}`, data);
export const deleteWebinar = (id) => api.delete(`/webinars/${id}`);
export const registerWebinar = (id) => api.post(`/webinars/${id}/register`);
export const unregisterWebinar = (id) => api.post(`/webinars/${id}/unregister`);

// AI Coder
export const generateIndicator = (data) => api.post('/ai/generate-indicator', data);
export const getAIHistory = (params) => api.get('/ai/history', { params });
export const getAIUsage = () => api.get('/ai/usage');
export const upgradeAICoderTier = (data) => api.post('/ai/upgrade-tier', data);

// Payment
export const createCheckout = (data) => api.post('/payment/create-checkout', data);
export const getSubscription = () => api.get('/payment/subscription');
export const cancelSubscription = () => api.post('/payment/cancel-subscription');
export const upgradeTier = (data) => api.post('/payment/upgrade-tier', data);

// Admin - User Management
export const getAllUsers = () => api.get('/admin/users');
export const upgradeUserTier = (userId, plan) => api.post('/admin/users/upgrade', { userId, plan });
export const downgradeUserTier = (userId) => api.post('/admin/users/downgrade', { userId });

// Admin - AI Usage Analytics
export const getAdminAIUsage = () => api.get('/admin/ai-usage');
export const getAdminAIUsageStats = () => api.get('/admin/ai-usage/stats');
export const getAdminAIUsageUsers = () => api.get('/admin/ai-usage/users');

// Notifications
export const getNotifications = () => api.get('/notifications');
export const getNotification = (id) => api.get(`/notifications/${id}`);
export const createNotification = (data) => api.post('/notifications', data);
export const updateNotification = (id, data) => api.put(`/notifications/${id}`, data);
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markNotificationUnread = (id) => api.put(`/notifications/${id}/unread`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const getAllNotificationsAdmin = () => api.get('/notifications/admin/all');

// Chat & Messages (New)
export const getMessages = (params) => api.get('/messages', { params });
export const createMessage = (data) => api.post('/messages', data);
export const updateMessage = (id, data) => api.put(`/messages/${id}`, data);
export const deleteMessage = (id) => api.delete(`/messages/${id}`);
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Knowledge Base (New)
export const getKBEntries = (params) => api.get('/knowledge-base', { params });
export const getKBEntry = (id) => api.get(`/knowledge-base/${id}`);
export const createKBEntry = (data) => api.post('/knowledge-base', data);
export const updateKBEntry = (id, data) => api.put(`/knowledge-base/${id}`, data);
export const deleteKBEntry = (id) => api.delete(`/knowledge-base/${id}`);

// Templates
export const getTemplates = () => api.get('/templates/user');
export const getTemplate = (id) => api.get(`/templates/${id}`);
export const incrementTemplateUsage = (id) => api.post(`/templates/${id}/increment-usage`);
export const createTemplate = (data) => api.post('/templates', data);
export const updateTemplate = (id, data) => api.put(`/templates/${id}`, data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);

// Push Notifications
export const sendPushNotification = (data) => api.post('/admin/push-notifications/send', data);

// Analytics
export const trackAnalytics = (data) => api.post('/analytics/track', data);
export const getMyAnalytics = (params) => api.get('/analytics/my-analytics', { params });
export const getAllAnalytics = (params) => api.get('/analytics/admin/all', { params });
export const getResourceAnalytics = (resourceId, params) => api.get(`/analytics/admin/resource/${resourceId}`, { params });
export const getAnalyticsStats = (params) => api.get('/analytics/admin/stats', { params });
export const getUserAnalytics = (userId, params) => api.get(`/analytics/admin/user/${userId}`, { params });

export default api;
